import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { accountsReceivableService, ARLedgerEntryDTO } from '../../../../services/accountingService';
import { customerService, CustomerDTO } from '../../../../services/customerService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { ArrowLeft } from 'lucide-react';

const ARCustomerLedgerPage: React.FC = () => {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();

  const [customer, setCustomer] = useState<CustomerDTO | null>(null);
  const [entries, setEntries] = useState<ARLedgerEntryDTO[]>([]);
  const [loading, setLoading] = useState(true);

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';
  const fmtDate = (d: string) => new Date(d).toLocaleDateString(langCode);

  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    Promise.all([
      customerService.getCustomerById(Number(customerId)).catch(() => null),
      accountsReceivableService.getCustomerLedger(Number(customerId)),
    ]).then(([custRes, ledgerRes]) => {
      if (custRes) setCustomer((custRes?.data as any)?.data ?? custRes?.data ?? null);
      const data = (ledgerRes?.data as any)?.data ?? ledgerRes?.data ?? [];
      setEntries(Array.isArray(data) ? data : []);
    }).finally(() => setLoading(false));
  }, [customerId]);

  return (
    <Layout>
      <div style={{ padding: '0 20px 40px', maxWidth: 900, margin: '0 auto' }}>
        <div className="ims-top-bar" style={{ marginBottom: 20 }}>
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/accounting/accounts-receivable')}>
              <ArrowLeft size={13} /> {t('accounting.ar.title')}
            </button>
            <div>
              <h1 className="ims-page-title">{customer?.name || t('common.loading')}</h1>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
          </div>
        ) : entries.length === 0 ? (
          <div className="ims-card" style={{ textAlign: 'center', padding: 40, color: 'var(--ims-text-muted)' }}>
            {t('accounting.ledger.noActivity')}
          </div>
        ) : (
          <div className="ims-card">
            <div className="ims-table-scroll">
              <table className="ims-table">
                <thead>
                  <tr>
                    <th className="ims-th" style={{ width: 110 }}>{t('accounting.ledger.date')}</th>
                    <th className="ims-th" style={{ width: 110 }}>{t('accounting.ar.transactionType')}</th>
                    <th className="ims-th" style={{ width: 140 }}>{t('accounting.ar.document')}</th>
                    <th className="ims-th">{t('common.description')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 120 }}>{t('accounting.ledger.debit')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 120 }}>{t('accounting.ledger.credit')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 130 }}>{t('accounting.ledger.balance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="ims-tr">
                      <td className="ims-td">{fmtDate(e.transactionDate)}</td>
                      <td className="ims-td">{t(`accounting.arTransactionType.${e.transactionType}`)}</td>
                      <td className="ims-td">{e.documentNumber ? <code>{e.documentNumber}</code> : '—'}</td>
                      <td className="ims-td">{e.description || '—'}</td>
                      <td className="ims-td ims-td--line-total">{e.debit > 0 ? fmt(e.debit) : ''}</td>
                      <td className="ims-td ims-td--line-total">{e.credit > 0 ? fmt(e.credit) : ''}</td>
                      <td className="ims-td ims-td--line-total"><strong>{fmt(e.runningBalance)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ARCustomerLedgerPage;
